import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Notices = () => {
  const [notices, setNotices] = useState([]);
  const [newNotice, setNewNotice] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editNoticeId, setEditNoticeId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Simulate user role - replace with your actual auth logic
  const role = localStorage.getItem('role') || 'student';
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/notices`);
      setNotices(response.data);
    } catch (error) {
      console.error('Error fetching notices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setNewNotice({ ...newNotice, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newNotice.title.trim() || !newNotice.content.trim()) {
      alert('Please fill in both title and content.');
      return;
    }

    setLoading(true);
    try {
      if (editMode) {
        // Update existing notice
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/notices/${editNoticeId}`,
          newNotice,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Notice updated successfully!');
      } else {
        // Add new notice
        await axios.post(`${process.env.REACT_APP_API_URL}/api/notices`, newNotice, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert('Notice posted successfully!');
      }
      
      fetchNotices(); // Refresh from database
      setNewNotice({ title: '', content: '' });
      setEditMode(false);
      setEditNoticeId(null);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error posting notice:', error);
      alert('Error posting notice.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (notice) => {
    setNewNotice({ title: notice.title, content: notice.content });
    setEditMode(true);
    setEditNoticeId(notice._id);
    setShowAddForm(true);
  };

  const handleDelete = async (noticeId) => {
    if (window.confirm('Are you sure you want to delete this notice?')) {
      setLoading(true);
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/notices/${noticeId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        fetchNotices(); // Refresh from database
        alert('Notice deleted successfully!');
      } catch (error) {
        console.error('Error deleting notice:', error);
        alert('Error deleting notice.');
      } finally {
        setLoading(false);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="notices-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .notices-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          overflow: hidden;
          padding: 2rem 1rem;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .notices-container::before {
          content: '';
          position: absolute;
          top: 15%;
          left: 8%;
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          animation: float 3s ease-in-out infinite;
        }

        .notices-container::after {
          content: '';
          position: absolute;
          top: 70%;
          right: 12%;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.05);
          animation: float 4s ease-in-out infinite;
          animation-delay: 1s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes noticeSlide {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        .pulse-element {
          position: absolute;
          bottom: 25%;
          left: 15%;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.08);
          animation: pulse 2s ease-in-out infinite;
        }

        .notices-wrapper {
          max-width: 900px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .header-section {
          text-align: center;
          margin-bottom: 3rem;
        }

        .header-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.15);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 1.5rem;
          backdrop-filter: blur(10px);
        }

        .main-title {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 900;
          color: white;
          margin-bottom: 0.5rem;
          text-shadow: 0 4px 20px rgba(0,0,0,0.3);
          animation: slideIn 0.8s ease-out;
        }

        .gradient-text {
          background: linear-gradient(45deg, #FFD700, #FF6B6B, #4ECDC4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-weight: 300;
          font-size: 1.1rem;
          animation: slideIn 0.8s ease-out 0.2s both;
        }

        .action-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .notices-count {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1rem;
          font-weight: 500;
        }

        .add-notice-btn {
          background: linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%);
          color: white;
          border: none;
          padding: 0.75rem 2rem;
          border-radius: 50px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: 0 4px 15px rgba(254, 107, 139, 0.3);
        }

        .add-notice-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(254, 107, 139, 0.4);
        }

        .form-container {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 2.5rem;
          margin-bottom: 2rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          animation: slideIn 0.5s ease-out;
          position: relative;
          overflow: hidden;
        }

        .form-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #FE6B8B, #FF8E53, #4ECDC4, #667eea);
          border-radius: 20px 20px 0 0;
        }

        .form-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .form-icon {
          width: 32px;
          height: 32px;
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: #374151;
          font-size: 0.9rem;
        }

        .form-input {
          width: 100%;
          padding: 1rem;
          border: 2px solid rgba(209, 213, 219, 0.3);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          font-family: inherit;
          font-size: 1rem;
          transition: all 0.3s ease;
          outline: none;
          box-sizing: border-box;
          resize: vertical;
        }

        .form-input:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          background: white;
        }

        .form-textarea {
          min-height: 120px;
          font-family: inherit;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .submit-btn {
          flex: 1;
          min-width: 150px;
          padding: 1rem 2rem;
          background: linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(254, 107, 139, 0.4);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .cancel-btn {
          padding: 1rem 2rem;
          background: rgba(107, 114, 128, 0.1);
          color: #374151;
          border: 2px solid rgba(107, 114, 128, 0.2);
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cancel-btn:hover {
          background: rgba(107, 114, 128, 0.15);
          border-color: rgba(107, 114, 128, 0.3);
        }

        .loading-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .notices-grid {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .notice-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 2rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          animation: noticeSlide 0.5s ease-out;
          position: relative;
          overflow: hidden;
        }

        .notice-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
        }

        .notice-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: linear-gradient(180deg, #FE6B8B, #FF8E53, #4ECDC4);
        }

        .notice-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 1rem;
          gap: 1rem;
        }

        .notice-main {
          flex: 1;
        }

        .notice-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
          margin-right: 1rem;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .notice-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        .notice-meta {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .notice-author {
          font-size: 0.85rem;
          color: #667eea;
          font-weight: 600;
          background: rgba(102, 126, 234, 0.1);
          padding: 0.25rem 0.75rem;
          border-radius: 50px;
        }

        .notice-date {
          font-size: 0.85rem;
          color: #6b7280;
          font-weight: 500;
        }

        .notice-content {
          color: #374151;
          line-height: 1.6;
          margin-bottom: 1.5rem;
          font-size: 1rem;
        }

        .notice-actions {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          width: 40px;
          height: 40px;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .edit-btn {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .edit-btn:hover {
          background: rgba(59, 130, 246, 0.2);
          transform: translateY(-2px);
        }

        .delete-btn {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .delete-btn:hover {
          background: rgba(239, 68, 68, 0.2);
          transform: translateY(-2px);
        }

        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 3rem;
        }

        .loading-content {
          text-align: center;
          color: white;
        }

        .loading-content .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top: 3px solid white;
          margin-bottom: 1rem;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-radius: 20px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .empty-icon {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: white;
        }

        .empty-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          margin-bottom: 0.5rem;
        }

        .empty-description {
          color: rgba(255, 255, 255, 0.8);
          font-size: 1rem;
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .notices-container {
            padding: 1rem 0.5rem;
          }
          
          .form-container {
            padding: 2rem 1.5rem;
          }
          
          .notice-card {
            padding: 1.5rem;
          }
          
          .notice-header {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .form-actions {
            flex-direction: column;
          }
          
          .submit-btn {
            min-width: unset;
          }
          
          .action-bar {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>

      <div className="pulse-element"></div>
      
      <div className="notices-wrapper">
        {/* Header Section */}
        <div className="header-section">
          <div className="header-chip">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M11,16.5L6.5,12L7.91,10.59L11,13.67L16.59,8.09L18,9.5L11,16.5Z"/>
            </svg>
            Important Announcements
          </div>
          
          <h1 className="main-title">
            <span className="gradient-text">Notice</span> Board
          </h1>
          
          <p className="subtitle">
            Stay updated with the latest announcements and important information
          </p>
        </div>

        {/* Action Bar */}
        <div className="action-bar">
          <div className="notices-count">
            {notices.length} {notices.length === 1 ? 'Notice' : 'Notices'} Available
          </div>
          
          {role === 'teacher' && (
            <button 
              className="add-notice-btn"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
              </svg>
              {showAddForm ? 'Cancel' : 'Add Notice'}
            </button>
          )}
        </div>

        {/* Add/Edit Notice Form */}
        {role === 'teacher' && showAddForm && (
          <div className="form-container">
            <div className="form-title">
              <div className="form-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                </svg>
              </div>
              {editMode ? 'Edit Notice' : 'Create New Notice'}
            </div>
            
            <div className="form-group">
              <label className="form-label">Notice Title *</label>
              <input
                type="text"
                name="title"
                className="form-input"
                placeholder="Enter a descriptive title for your notice"
                value={newNotice.title}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Notice Content *</label>
              <textarea
                name="content"
                className="form-input form-textarea"
                placeholder="Write the detailed content of your notice here..."
                value={newNotice.content}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-actions">
              <button
                className="submit-btn"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading && <div className="loading-spinner"></div>}
                {loading 
                  ? (editMode ? 'Updating...' : 'Publishing...') 
                  : (editMode ? 'Update Notice' : 'Publish Notice')
                }
              </button>
              
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowAddForm(false);
                  setEditMode(false);
                  setNewNotice({ title: '', content: '' });
                  setEditNoticeId(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Notices Display */}
        {loading ? (
          <div className="loading-container">
            <div className="loading-content">
              <div className="loading-spinner"></div>
              <p>Loading notices...</p>
            </div>
          </div>
        ) : notices.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
              </svg>
            </div>
            <h3 className="empty-title">No Notices Available</h3>
            <p className="empty-description">
              There are currently no notices to display. Check back later for updates.
            </p>
          </div>
        ) : (
          <div className="notices-grid">
            {notices.map((notice, index) => (
              <div key={notice._id} className="notice-card" style={{animationDelay: `${index * 0.1}s`}}>
                <div className="notice-header">
                  <div style={{display: 'flex', alignItems: 'flex-start', flex: 1}}>
                    <div className="notice-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L6.5,12L7.91,10.59L11,13.67L16.59,8.09L18,9.5L11,16.5Z"/>
                      </svg>
                    </div>
                    <div className="notice-main">
                      <h3 className="notice-title">{notice.title}</h3>
                      <div className="notice-meta">
                        <span className="notice-author">{notice.author}</span>
                        <span className="notice-date">{formatDate(notice.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {role === 'teacher' && (
                    <div className="notice-actions">
                      <button 
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(notice)}
                        title="Edit notice"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"/>
                        </svg>
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(notice._id)}
                        title="Delete notice"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="notice-content">
                  {notice.content}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notices;